import { afterNextRender, ElementRef, Injector } from "@angular/core";

import {
  differenceInCalendarDays,
  differenceInMonths,
  differenceInYears,
  startOfDay,
} from "date-fns";

const ago = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? "" : "s"} ago`;

/**
 * Relative "time ago" label for the today-view datepicker, based on how far in the
 * past the selected date is:
 *   today -> "Today"; 1-6 days -> "N day(s) ago"; 7-28 days -> "N week(s) ago" (1-4);
 *   otherwise months/years -> "N month(s) ago" / "N year(s) ago".
 */
export function relativeDateLabel(selected: Date, now: Date = new Date()): string {
  // Strip time: the selected date carries an end-of-day time, which would otherwise
  // make differenceInMonths/Years floor down a partial period (e.g. Apr 4 -> Aug 4 = 3).
  const from = startOfDay(selected);
  const to = startOfDay(now);

  const days = differenceInCalendarDays(to, from);
  if (days <= 0) {
    return "Today";
  }
  if (days <= 6) {
    return ago(days, "day");
  }
  if (days <= 28) {
    return ago(Math.floor(days / 7), "week");
  }

  const years = differenceInYears(to, from);
  if (years >= 1) {
    return ago(years, "year");
  }
  // Elapsed (age-style) months, min 1 for the 29-days-to-1-month gap.
  return ago(Math.max(1, differenceInMonths(to, from)), "month");
}

export function isMobileScreen(): boolean {
  // xs = 474px in tailwind config; below this the end picker is left-anchored
  // (xs:tw-right-auto), so the left-anchored translate math is valid.
  return window.innerWidth <= 474;
}

/**
 * Calculates the horizontal translate value to center a calendar picker in the viewport
 * when it would otherwise go off-screen.
 * @param triggerRef - The element that triggers the calendar (e.g., the date input button)
 * @param pickerRef - The calendar picker element
 * @returns A CSS translate value string (e.g., "0" or "-100px")
 */
export function calculatePickerTranslateValue(
  triggerRef: ElementRef | undefined,
  pickerRef: ElementRef | undefined,
): string {
  if (!triggerRef || !pickerRef) {
    return "0";
  }

  const triggerRect = triggerRef.nativeElement.getBoundingClientRect();
  const calendarRect = pickerRef.nativeElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const calendarLeft = triggerRect.left;
  const calendarRight = triggerRect.left + calendarRect.width;

  // Check if calendar goes off-screen (left or right)
  if (calendarLeft < 0 || calendarRight > viewportWidth) {
    // Center the calendar horizontally in the viewport
    const viewportCenter = viewportWidth / 2;
    const calendarCenter = calendarRect.width / 2;
    const targetLeft = viewportCenter - calendarCenter;
    const currentLeft = triggerRect.left;
    const translateValue = targetLeft - currentLeft;
    return `${translateValue}px`;
  }

  // Calendar fits within viewport, no translation needed
  return "0";
}

/**
 * Closing a picker removes its calendar - and the Confirm/Cancel button (or day) that had focus -
 * so focus would fall to <body> (#6245). Hands it back to `trigger` once the calendar is gone, but
 * only when focus was in that picker: its trigger's wrapper, which holds the trigger and its
 * calendar. A mouse click outside, a Tab out, or focus on the other picker's trigger has already
 * put focus where the user wanted it.
 */
export function refocusPickerTrigger(
  trigger: HTMLElement | null | undefined,
  injector: Injector,
): void {
  const picker = trigger?.parentElement;
  if (picker?.contains(document.activeElement)) {
    afterNextRender(
      () => {
        // Unless focus moved on meanwhile: it's still where the calendar left it (in the picker,
        // or <body> once the focused control is gone).
        const active = document.activeElement;
        if (!active || active === document.body || picker.contains(active)) {
          trigger.focus();
        }
      },
      { injector },
    );
  }
}

/**
 * Whether an Escape belongs to a dropdown open in the calendar (its year/month selects) rather
 * than to the picker: that dropdown closes itself on Escape and hands focus to its own trigger,
 * so the picker stays open. Decided from where the key came from (its options list, kept on the
 * event even once the list is gone), else from the dropdown still being marked expanded - for an
 * Escape that didn't come from it (e.g. focus on <body> after a mouse click).
 */
export function escapeBelongsToInnerDropdown(host: HTMLElement, event?: Event): boolean {
  const target = event?.target;
  if (target instanceof Element && target.closest('[role="listbox"]')) {
    return true;
  }
  return !!host.querySelector('app-calendar [aria-haspopup="listbox"][aria-expanded="true"]');
}
